import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Building2, ChevronDown, CirclePlus } from 'lucide-react';

import { useFieldWiring } from '@nesso/ui/hooks/use-field-wiring';
import { cn } from '@nesso/ui/lib/utils';
import {
    Command,
    CommandGroup,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@nesso/ui/primitives/command';
import { Field, FieldSlot } from '@nesso/ui/primitives/field';
import { inputInvalidClassName } from '@nesso/ui/primitives/input';
import { Popover, PopoverContent, PopoverTrigger } from '@nesso/ui/primitives/popover';
import { selectTriggerClassName } from '@nesso/ui/primitives/select';

import { practiceLocationsOptions } from '@/entities/practice';
import { useSheetStore } from '@/shared/stores/sheet-store';

type Props = {
    value: string;
    error?: boolean | string;
    onSelect: (practiceLocationId: string) => void;
};

// Popover + Command, not Combobox — the design has a search-less list under a select-style
// trigger plus an "Add new location" action `Combobox` has no slot for.
export function ScheduleSessionLocationPicker({ value, error, onSelect }: Props) {
    const [open, setOpen] = useState(false);
    const { data: locations } = useQuery(practiceLocationsOptions());
    const wiring = useFieldWiring({ error });
    const navigate = useNavigate();
    const closeAllSheets = useSheetStore((state) => state.closeAllSheets);

    const options = locations ?? [];
    const selected = options.find((location) => location.id === value);
    const triggerLabel = selected
        ? `${selected.name}${selected.isDefault ? ' (default)' : ''}`
        : 'Select location';

    function selectOption(practiceLocationId: string) {
        onSelect(practiceLocationId);
        setOpen(false);
    }

    function addNewLocation() {
        closeAllSheets();
        navigate({ to: '/settings/practice-details' });
    }

    return (
        <Field data-invalid={wiring.dataInvalid}>
            {/* modal: gives the portaled list its own scroll lock; the dialog's lock blocks wheel and touch. */}
            <Popover open={open} onOpenChange={setOpen} modal={true}>
                <PopoverTrigger
                    {...wiring.controlProps}
                    type="button"
                    className={cn(
                        selectTriggerClassName,
                        inputInvalidClassName,
                        'bg-input-background justify-between gap-2',
                    )}
                >
                    <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="flex-1 truncate text-left">{triggerLabel}</span>
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                </PopoverTrigger>
                <PopoverContent
                    className="w-(--radix-popover-trigger-width) max-w-(--radix-popover-content-available-width) p-0"
                    align="start"
                    collisionPadding={24}
                >
                    {/* No search: `shouldFilter={false}` keeps the list in the practice's own
                    order instead of cmdk's scoring. */}
                    <Command shouldFilter={false}>
                        <CommandList>
                            {options.length > 0 && (
                                <>
                                    <CommandGroup>
                                        {options.map((location) => (
                                            <CommandItem
                                                key={location.id}
                                                value={location.id}
                                                onSelect={() => selectOption(location.id)}
                                                // Overrides the primitive's default 44px row height.
                                                className="py-1.5"
                                            >
                                                <span className="truncate">
                                                    {location.name}
                                                    {location.isDefault && ' (default)'}
                                                </span>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                    <CommandSeparator />
                                </>
                            )}
                            <CommandGroup>
                                <CommandItem onSelect={addNewLocation} className="py-1.5">
                                    <CirclePlus className="h-4 w-4" />
                                    Add new location
                                </CommandItem>
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
            <FieldSlot errorMessage={wiring.errorMessage} errorId={wiring.errorId} />
        </Field>
    );
}
